import { commentsStore } from "../hooks/useCommentsStore";
import { occurrencePreviewPath } from "./eventDomain";

// Notification.TargetType.EVENT_OCCURRENCE on the backend.
const EVENT_OCCURRENCE_TARGET = "event_occurrence";
// Notification.TargetType.EVENT_OCCURRENCE_COMMENT: a comment on an event
// occurrence (never a Post comment — the IDs are a different table's). Its
// occurrence is the notification's occurrence_id.
const EVENT_OCCURRENCE_COMMENT_TARGET = "event_occurrence_comment";

// Engagement with the viewer's event (they host it): they open the
// occurrence preview, where the likes and reactions are, rather than the
// full detail other occurrence notifications open.
const OCCURRENCE_PREVIEW_TYPES = new Set(["event_like", "event_reaction"]);
// Misspelling this file used to match; kept so nothing that still sends it breaks.
const LEGACY_EVENT_OCCURRENCE_TARGET = "event_occurence";

// "/posts/91/" → 91. Only for comment notifications without post_id.
function postIdFromUrl(url) {
  const match = /\/posts\/(\d+)/.exec(url || "");
  return match ? Number(match[1]) : null;
}

const ROUTABLE_TARGET_TYPES = new Set([
  "post",
  "comment",
  "thread",
  "event",
  EVENT_OCCURRENCE_TARGET,
  LEGACY_EVENT_OCCURRENCE_TARGET,
]);

// Whether clicking the notification can go anywhere. False for ones with no
// target — event_deleted, or any whose Event/occurrence was deleted later
// (the backend clears target_type/target_id) — which show as plain text.
export function hasNotificationTarget(notification) {
  if (!notification) return false;
  if (notification.target_type === EVENT_OCCURRENCE_COMMENT_TARGET) {
    return notification.target_id != null && notification.occurrence_id != null;
  }
  if (ROUTABLE_TARGET_TYPES.has(notification.target_type) && notification.target_id != null) {
    return true;
  }
  return Boolean(notification.url);
}

// The event or occurrence a notification points at, from target_type /
// target_id only — never its message text:
//   { kind: "event", eventId }           invites, series RSVPs, series
//                                        reschedules, event_series_ended, …
//   { kind: "occurrence", occurrenceId } event_start, event_reminder,
//                                        event_canceled, occurrence invites
//                                        and RSVPs, and a reschedule naming
//                                        one or several dates (it targets the
//                                        first; there's no multi-date review)
//   null                                 anything else, or no target — e.g.
//                                        an untargeted cancellation (an
//                                        occurrence-only guest's dates deleted
//                                        with the series) or event_deleted
// An occurrence target is opened by occurrence id alone: it needs no access
// to the parent Event (see occurrenceActions.loadEventDetailAccess).
export function eventNotificationTarget(notification) {
  const targetId = notification?.target_id;
  if (targetId == null) return null;
  switch (notification.target_type) {
    case "event":
      return { kind: "event", eventId: targetId };
    case EVENT_OCCURRENCE_TARGET:
    case LEGACY_EVENT_OCCURRENCE_TARGET:
      return { kind: "occurrence", occurrenceId: targetId };
    default:
      return null;
  }
}

// Centralized mapping from a Notification's canonical target
// (target_type + target_id) to a React destination. This is the ONLY
// place that should branch on target_type — components call
// routeNotification(...) rather than re-implementing this switch.
//
// notification.notification_type explains WHY the notification exists
// (comment, like, reply, event_rsvp, ...). target_type/target_id explain
// WHERE clicking it should go. Multiple notification_types can (and do,
// per the backend) share the same target — keep these separate.
//
// `url` is legacy (backend-generated, being phased out). It is used only
// as a fallback when target_type/target_id can't produce a destination on
// their own — never when they can.
//
// Event targets never touch the Calendar/FullCalendar: they open the shared
// EventFocusModal by id, which resolves the Event (and occurrence) itself
// and shows a "no longer available" state for missing/deleted targets.
export async function routeNotification(notification, { navigate, openModal, token }) {
  const targetType = notification?.target_type;
  const targetId = notification?.target_id;
  const legacyUrl = notification?.url;

  switch (targetType) {
    case "post": {
      if (targetId == null) break;
      navigate(`/posts/${targetId}/`);
      return;
    }

    case "comment": {
      // New comments, replies, comment likes and comment reactions, and
      // post_comment_mention (post_mention targets the post). target_id is
      // the exact comment (never a post); post_id is its post. post_id is null
      // once the comment is hard-deleted, so the legacy url stands in for it.
      const postId = notification.post_id ?? postIdFromUrl(legacyUrl);
      if (targetId == null || postId == null) break;
      try {
        // Load the comment's /context/ before navigating: the conversation
        // then opens straight from the cache, and a comment that's hidden
        // (block) or gone opens the post instead of an error screen.
        if (token) commentsStore.ensureSession(token);
        await commentsStore.refreshThread({ type: "post", id: postId }, targetId);
        navigate(`/posts/${postId}/?comment=${targetId}`);
      } catch {
        navigate(`/posts/${postId}/`);
      }
      return;
    }

    case "thread": {
      // No current backend caller produces this yet (reserved for future
      // message notifications), but it's a declared TargetType, so it's
      // handled here rather than silently falling through.
      if (targetId == null) break;
      navigate(`/messages/t/${targetId}/`);
      return;
    }

    case "event": {
      // target_id is an Event id: invites, RSVPs, series reschedules
      // (event_rescheduled), event_series_ended (the surviving Event), and
      // event_start notifications created before they became
      // occurrence-targeted.
      // ModalManager wraps the whole authenticated app, so this opens
      // correctly from any page — no need to navigate to /calendar first.
      const target = eventNotificationTarget(notification);
      if (!target) break;
      openModal("EventFocusModal", { eventId: target.eventId });
      return;
    }

    case EVENT_OCCURRENCE_TARGET:
    case LEGACY_EVENT_OCCURRENCE_TARGET: {
      // target_id is an EventOccurrence id: event_start, event_reminder,
      // event_canceled (opens the canceled occurrence, read-only),
      // occurrence invitations and RSVPs, and
      // event_rescheduled (one date, or the first of several). The modal
      // fetches /event-occurrences/<id>/ and then — only if the viewer can
      // see the series — its Event (loadEventDetailAccess); a guest invited
      // to this occurrence only gets its standalone detail. A regenerated or
      // deleted occurrence 404s → shown as no longer available; no
      // replacement occurrence is guessed.
      const target = eventNotificationTarget(notification);
      if (!target) break;
      if (OCCURRENCE_PREVIEW_TYPES.has(notification.notification_type)) {
        navigate(occurrencePreviewPath(target.occurrenceId));
        return;
      }
      openModal("EventFocusModal", { occurrenceId: target.occurrenceId });
      return;
    }

    case EVENT_OCCURRENCE_COMMENT_TARGET: {
      // event_comment, event_reply, event_comment_like,
      // event_comment_reaction, event_comment_mention. target_id is an occurrence comment and
      // occurrence_id its occurrence; with either missing (the comment or
      // occurrence was deleted) there's nowhere to go.
      const occurrenceId = notification.occurrence_id;
      if (targetId == null || occurrenceId == null) return;
      const path = occurrencePreviewPath(occurrenceId);
      try {
        // As for Post comments: load /context/ first, so the conversation
        // opens from the cache, and a comment that's hidden or gone — or an
        // occurrence the viewer can no longer see — opens the preview, which
        // says what's still available.
        if (token) commentsStore.ensureSession(token);
        await commentsStore.refreshThread({ type: "occurrence", id: occurrenceId }, targetId);
        navigate(`${path}?comment=${targetId}`);
      } catch {
        navigate(path);
      }
      return;
    }

    case "profile": {
      // target_id here is a raw numeric user id, but the only React
      // profile route is /profiles/:username — nothing else in the
      // notification payload reliably provides the target's username
      // (notification.sender matches it for today's one caller,
      // friend_request, but that's incidental to that call site, not a
      // guaranteed contract, so it isn't relied on). Legacy `url` is the
      // sanctioned exception here — see report to the user for the
      // backend contract gap this papers over.
      break;
    }

    default: {
      if (targetType) {
        // Unknown/future target_type the frontend doesn't understand yet.
        // Fail safe: no navigation, no guessed URL, notification stays usable.
        console.warn("Unhandled notification target_type:", targetType);
      }
      break;
    }
  }

  // Legacy fallback — notifications predating target_type/target_id, or
  // (temporarily) profile notifications per the gap noted above.
  if (legacyUrl) {
    navigate(legacyUrl);
  }

  // Otherwise: no usable destination. Leave the notification visible/read,
  // just don't navigate anywhere.
}
